/**
 * Opt-in persistence of a donor's identity and receipt information so repeat
 * donations only require an amount, a message and a bidwar choice.
 * Everything lives in the donor's own browser; nothing is sent anywhere.
 */

const STORAGE_KEY = 'donationDonorInfo';
/** Bump when the stored shape changes. Older entries are discarded on load. */
const STORAGE_VERSION = 1;

/**
 * The only donation form fields that may be persisted between donations.
 * Anything absent from this list (`amount`, `message`, `bidwarChoices`, ...) is
 * specific to a single donation and must never be written to storage.
 * Booleans are listed separately so loaded values can be type-checked.
 */
const SAVED_STRING_FIELDS = [
  'username',
  'donorType',
  'userFullName',
  'userCompany',
  'userEmail',
  'userAddress',
  'userCity',
  'userProvince',
  'userPostalCode',
  'userCountry',
];
const SAVED_BOOLEAN_FIELDS = ['isAnonymous'];

/**
 * The persistable subset of a donation form's data.
 * @param {Record<string, unknown>} formData
 */
export function pickDonorFields(formData) {
  /** @type {Record<string, unknown>} */
  const donorFields = {};
  for (const field of SAVED_STRING_FIELDS) {
    if (typeof formData[field] === 'string') donorFields[field] = formData[field];
  }
  for (const field of SAVED_BOOLEAN_FIELDS) {
    if (typeof formData[field] === 'boolean') donorFields[field] = formData[field];
  }

  return donorFields;
}

export function clearDonorInfo() {
  if (globalThis.window === undefined) return;
  try {
    globalThis.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (Safari private browsing).
    // Remembering the information is a convenience, never block the donation.
  }
}

/**
 * Previously saved donor information, or `null` if there is none, it is
 * unreadable, or it was written by an older version of this module.
 * Values are type-checked: storage is user-writable and its content ends up in
 * the charge creation payload.
 * @returns {Record<string, unknown> | null}
 */
export function loadDonorInfo() {
  if (globalThis.window === undefined) return null;
  try {
    const rawEntry = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!rawEntry) return null;

    const entry = JSON.parse(rawEntry);
    if (entry?.version !== STORAGE_VERSION || typeof entry.data !== 'object' || !entry.data) {
      clearDonorInfo();

      return null;
    }

    const donorFields = pickDonorFields(entry.data);

    return Object.keys(donorFields).length > 0 ? donorFields : null;
  } catch {
    // Corrupt or hand-edited entry. Start over rather than break the form.
    clearDonorInfo();

    return null;
  }
}

/** @param {Record<string, unknown>} formData */
export function saveDonorInfo(formData) {
  if (globalThis.window === undefined) return;
  try {
    globalThis.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: STORAGE_VERSION, data: pickDonorFields(formData) }),
    );
  } catch {
    // Storage can be full or unavailable. See clearDonorInfo.
  }
}
